/**
 * Maps API/axios errors to safe user-facing strings (no SQL/ORM leakage).
 */

export function isUnsafeServerMessageForEndUser(message: string): boolean {
  if (!message?.trim()) return true;
  const m = message.toLowerCase();
  const hints = [
    "does not exist",
    "syntax error",
    "violates ",
    "duplicate key",
    "deadlock",
    "relation ",
    "invalid input syntax",
    "foreign key constraint",
    "null value in column",
    "column ",
    "queryfailed",
    "connection refused",
    "econnrefused",
    "severity:",
    "postgres",
    "ora-",
  ];
  if (hints.some((h) => m.includes(h))) return true;
  if (/__[a-z0-9_]+__/i.test(message)) return true;
  return false;
}

function normalizeAxiosResponseMessage(data: unknown): string {
  if (!data || typeof data !== "object") return "";
  const d = data as Record<string, unknown>;
  const msg = d.message;
  if (typeof msg === "string") return msg.trim();
  if (Array.isArray(msg)) {
    return msg
      .filter((x) => typeof x === "string")
      .join(" ")
      .trim();
  }
  const err = d.error;
  if (typeof err === "string") return err.trim();
  if (err && typeof err === "object" && "message" in err) {
    const em = (err as { message?: unknown }).message;
    if (typeof em === "string") return em.trim();
  }
  return "";
}

export function getAxiosErrorUserMessage(error: unknown): string {
  if (error && typeof error === "object" && "response" in error) {
    const res = (
      error as {
        response?: { data?: unknown; status?: number };
      }
    ).response;
    const status = res?.status;
    const serverMessage = normalizeAxiosResponseMessage(res?.data);
    if (
      serverMessage &&
      !isUnsafeServerMessageForEndUser(serverMessage) &&
      serverMessage.length <= 280
    ) {
      return serverMessage;
    }
    if (status === 403) return "Privacy consent is required to use the chat.";
    if (status === 429)
      return "Too many requests. Please try again in a moment.";
    if (status != null && status >= 500)
      return "Something went wrong on our end. Please try again later.";
    if (status === 401)
      return "Your session has expired. Please sign in again.";
    if (status === 404)
      return "The requested resource was not found. Please try again.";
    if (status != null && status >= 400)
      return "We couldn't complete this request. Please try again.";
  }
  if (error instanceof Error) {
    const msg = error.message;
    if (!isUnsafeServerMessageForEndUser(msg)) return msg;
  }
  return "Something went wrong. Please try again.";
}
